import React, { useState, useTransition, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { signIn, signOut } from "../../lib/auth";
import { getApiClient } from "../../api/api-client";
import { API_ENDPOINTS } from "../../api/endpoints";
import { getQueryClient } from "../../providers/QueryProvider";
import { useAuth } from "../../contexts/AuthContext";
import { loginSchema, type LoginFormData } from "../../schemas/auth.schema";
import type { PrivacyPolicySyncState } from "../../api/types/auth";
import Button from "../../components/ui/Button";
import Input from "../../components/ui/Input";
import Icon from "../../components/AppIcon";
import { useEmailSpellCheck } from "../../hooks/useEmailSpellCheck";

const Login: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { refresh } = useAuth();
  const [isPending, startTransition] = useTransition();
  const [error, setError] = useState<string>("");
  const [showPassword, setShowPassword] = useState(false);
  const [sessionExpiredMessage, setSessionExpiredMessage] =
    useState<string>("");

  const { suggestion, checkEmail, applySuggestion } = useEmailSpellCheck();

  // React Hook Form with Zod validation
  const {
    register,
    handleSubmit: handleFormSubmit,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: "",
      password: "",
      rememberMe: false,
    },
  });

  // Handle session expiration messages
  useEffect(() => {
    // Check for error message from HTTP client (stored in sessionStorage)
    const storedErrorMessage = sessionStorage.getItem("auth:errorMessage");
    const storedErrorReason = sessionStorage.getItem("auth:errorReason");

    if (storedErrorMessage) {
      console.log("🚨 Login: Session expiration detected:", {
        reason: storedErrorReason,
        message: storedErrorMessage,
      });

      setSessionExpiredMessage(storedErrorMessage);

      // Clear the stored error message after displaying
      sessionStorage.removeItem("auth:errorMessage");
      sessionStorage.removeItem("auth:errorReason");
      return;
    }

    // Fallback: Check URL parameters for legacy support
    const reason = searchParams.get("reason");
    if (reason) {
      const messages: Record<string, string> = {
        token_expired: "Your session has expired. Please log in again.",
        session_expired: "Your session has expired. Please log in again.",
        device_conflict:
          "You have been logged in from another device. Please log in again.",
        session_invalid: "Your session is invalid. Please log in again.",
        refresh_token_invalid:
          "Your session is no longer valid. Please log in again.",
        refresh_failed: "Unable to refresh your session. Please log in again.",
        no_valid_token_after_refresh:
          "Session refresh failed. Please log in again.",
        refresh_token_error: "Your session has expired. Please log in again.",
        no_refresh_handler: "Session management error. Please log in again.",
      };

      const message =
        messages[reason] || "Your session has ended. Please log in again.";
      setSessionExpiredMessage(message);

      // Clear the reason from URL after showing message
      const newUrl = window.location.pathname;
      window.history.replaceState({}, "", newUrl);
    }
  }, [searchParams]);

  const handleSubmit = async (data: LoginFormData) => {
    setError("");
    console.log("🔐 Login: Form submitted");

    startTransition(async () => {
      try {
        // Sign out any existing session first to prevent cookie conflicts
        // when a different user logs in on the same browser
        console.log(
          "📡 Login: Signing out any existing session before login...",
        );
        try {
          await signOut();
          console.log("✅ Login: Existing session cleared");
        } catch {
          // No active session — that's fine, continue with login
          console.log("ℹ️ Login: No existing session to clear");
        }

        console.log("📡 Login: Making login request with Better Auth...");

        // Use Better Auth signIn.email() method
        const result = await signIn.email({
          email: data.email,
          password: data.password,
          rememberMe: data.rememberMe,
        });

        console.log("📨 Login: Response received:", {
          hasData: !!result.data,
          hasUser: !!result.data?.user,
        });

        // Better Auth returns: { data: { user, session } }
        const user = result.data?.user;

        if (!user) {
          console.error("❌ Login: No user data in response");
          setError(
            "Invalid email or password. Please check your credentials and try again.",
          );
          return;
        }

        const userRole = user.role;

        console.log("✅ Login: Valid response received, user authenticated");

        // Wait for the shared session to hold the new user before navigating,
        // so the route guards don't see a signed-out state and bounce back here.
        await refresh();

        // Clear React Query cache so stale data from the previous user
        // doesn't bleed into the new user's session
        console.log("🧹 Login: Clearing React Query cache...");
        getQueryClient().clear();
        console.log("✅ Login: Cache cleared");

        // Check whether the newly signed-in user must accept an updated
        // privacy policy before entering their dashboard.
        try {
          const policyState = await getApiClient()
            .getHttpClient()
            .get<PrivacyPolicySyncState>(API_ENDPOINTS.PRIVACY_POLICY.ME);

          if (policyState?.requiresAcceptance) {
            console.log("📜 Login: Privacy policy acceptance required:", {
              role: userRole,
              version: policyState.policy?.version,
              acceptedVersion: policyState.acceptedVersion,
            });

            navigate("/auth/privacy-policy", {
              replace: true,
              state: {
                nextPath:
                  userRole === "ADMIN"
                    ? "/admin/platform-overview"
                    : "/teacher/overview",
              },
            });
            return;
          }
        } catch (policyError) {
          console.warn("⚠️ Login: Privacy policy check failed, continuing:", policyError);
        }

        // Role-based redirection
        let redirectPath: string;
        switch (userRole) {
          case "ADMIN":
            redirectPath = "/admin/platform-overview";
            break;
          case "TEACHER":
            redirectPath = "/teacher/overview";
            break;
          default:
            // For STUDENT or any other role, redirect to login
            redirectPath = "/auth/login";
            break;
        }

        console.log("🔄 Login: Navigating to:", redirectPath);
        navigate(redirectPath, { replace: true });
        console.log("✅ Login: Navigation completed");
      } catch (error: any) {
        console.error("❌ Login: Login failed:", error);

        // Better error messages based on error type
        let errorMessage =
          "Invalid email or password. Please check your credentials and try again.";

        if (error.response?.status === 401) {
          errorMessage =
            "Invalid email or password. Please check your credentials and try again.";
        } else if (error.response?.status === 403) {
          const serverMessage: string = error.response?.data?.message ?? "";
          if (serverMessage.toLowerCase().includes("banned")) {
            errorMessage =
              "Your account has been banned. Please contact support.";
          } else if (serverMessage.toLowerCase().includes("email")) {
            errorMessage =
              "Your email address is not verified. Please verify your email before signing in.";
          } else if (serverMessage.toLowerCase().includes("phone")) {
            errorMessage =
              "Your phone number is not verified. Please complete onboarding first.";
          } else {
            errorMessage =
              serverMessage || "Access denied. Please contact support.";
          }
        } else if (error.response?.status === 429) {
          errorMessage = "Too many login attempts. Please try again later.";
        } else if (error.response?.status === 500) {
          errorMessage = "Server error. Please try again later.";
        } else if (error.message?.includes("Network")) {
          errorMessage =
            "Network error. Please check your internet connection.";
        } else if (error.response?.data?.message) {
          // Use server message if available, but sanitize it
          const serverMessage = error.response.data.message;
          if (
            serverMessage.toLowerCase().includes("user not found") ||
            serverMessage.toLowerCase().includes("invalid credentials") ||
            serverMessage.toLowerCase().includes("incorrect password")
          ) {
            errorMessage =
              "Invalid email or password. Please check your credentials and try again.";
          } else {
            errorMessage = serverMessage;
          }
        }

        setError(errorMessage);
      }
    });
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-linear-to-br from-indigo-50 via-white to-cyan-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8 bg-white p-8 rounded-xl shadow-lg border border-gray-100">
        <div className="text-center">
          <div className="mx-auto h-12 w-12 bg-primary/10 rounded-full flex items-center justify-center mb-4">
            <Icon name="Lock" size={24} className="text-primary" />
          </div>
          <h2 className="text-3xl font-bold text-gray-900 tracking-tight">
            Welcome Back
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            Sign in to access your dashboard
          </p>
        </div>

        <form
          className="mt-8 space-y-6"
          onSubmit={handleFormSubmit(handleSubmit)}
        >
          {sessionExpiredMessage && (
            <div
              className={`rounded-md p-4 mb-4 ${
                sessionExpiredMessage.toLowerCase().includes("device")
                  ? "bg-orange-50"
                  : "bg-yellow-50"
              }`}
            >
              <div className="flex">
                <Icon
                  name={
                    sessionExpiredMessage.toLowerCase().includes("device")
                      ? "Smartphone"
                      : "AlertTriangle"
                  }
                  size={20}
                  className={`mr-2 ${
                    sessionExpiredMessage.toLowerCase().includes("device")
                      ? "text-orange-400"
                      : "text-yellow-400"
                  }`}
                />
                <div
                  className={`text-sm ${
                    sessionExpiredMessage.toLowerCase().includes("device")
                      ? "text-orange-700"
                      : "text-yellow-700"
                  }`}
                >
                  {sessionExpiredMessage}
                </div>
              </div>
            </div>
          )}

          <div className="space-y-4">
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Email address
              </label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                placeholder="Enter your email"
                disabled={isPending || isSubmitting}
                {...register("email")}
                onChange={(e) => {
                  register("email").onChange(e);
                  checkEmail(e.target.value);
                }}
                suggestion={suggestion?.full}
                onSuggestionClick={() => {
                  const newVal = applySuggestion();
                  if (newVal) {
                    setValue("email", newVal, { shouldValidate: true });
                  }
                }}
                className={
                  errors.email ? "border-red-500 focus:ring-red-500" : ""
                }
              />
              {errors.email && (
                <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                  <Icon name="AlertCircle" size={14} />
                  {errors.email.message}
                </p>
              )}
            </div>

            <div>
              <label
                htmlFor="password"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Password
              </label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  placeholder="Enter your password"
                  disabled={isPending || isSubmitting}
                  {...register("password")}
                  className={
                    errors.password ? "border-red-500 focus:ring-red-500" : ""
                  }
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  disabled={isPending || isSubmitting}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  <Icon name={showPassword ? "EyeOff" : "Eye"} size={18} />
                </button>
              </div>
              {errors.password && (
                <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                  <Icon name="AlertCircle" size={14} />
                  {errors.password.message}
                </p>
              )}
            </div>

            <div className="flex items-center">
              <input
                id="rememberMe"
                type="checkbox"
                {...register("rememberMe")}
                disabled={isPending || isSubmitting}
                className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded disabled:opacity-50"
              />
              <label
                htmlFor="rememberMe"
                className="ml-2 block text-sm text-gray-700"
              >
                Remember me for 30 days
              </label>
            </div>
          </div>

          {error && (
            <div className="rounded-md bg-red-50 p-4 border border-red-200">
              <div className="flex items-start">
                <Icon
                  name="AlertCircle"
                  size={20}
                  className="text-red-600 mr-2 shrink-0 mt-0.5"
                />
                <div className="text-sm text-red-700">{error}</div>
              </div>
            </div>
          )}

          <div>
            <Button
              type="submit"
              disabled={isPending || isSubmitting}
              className="w-full"
            >
              {isPending || isSubmitting ? (
                <span className="flex items-center justify-center gap-2">
                  <Icon name="Loader2" size={18} className="animate-spin" />
                  Signing in...
                </span>
              ) : (
                "Sign in"
              )}
            </Button>
          </div>

          {/* Signup Link */}
          <div className="text-center">
            <p className="text-sm text-gray-600">
              Don't have an account?{" "}
              <Link
                to="/auth/signup/teacher"
                className="font-medium text-primary hover:text-primary/80 transition-colors"
              >
                Sign up as Teacher
              </Link>
            </p>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Login;
